import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function PreviewBanner() {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thankOpen, setThankOpen] = useState(false);
  const [error, setError] = useState("");

  // Don't show banner in admin/adminX routes
  if (pathname.startsWith("/adminX") || pathname.startsWith("/admin")) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = (message || "").trim();
    if (!trimmed) { setError(t("banner.errorEmpty")); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", { message: trimmed, lang, page: pathname });
      setMessage("");
      setThankOpen(true);
    } catch (err) {
      if (err.response?.status === 429) setError(t("banner.errorRate"));
      else setError(t("banner.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section
        data-testid="preview-banner"
        className="bg-gradient-to-r from-[#FFF1F4] via-[#FDF7E7] to-[#F0F9FF] border-b border-zinc-200/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-7 flex items-start gap-3">
            <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center">
              <AlertCircle className="w-4 h-4" />
            </div>
            <div className="text-sm text-zinc-800 leading-relaxed">
              <div data-testid="banner-welcome" className="font-semibold text-zinc-900">
                {t("banner.welcome")}
              </div>
              <div data-testid="banner-hint" className="mt-1 text-zinc-600">
                {t("banner.hint")}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-5 flex flex-col gap-2">
            <Textarea
              data-testid="feedback-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("banner.placeholder")}
              rows={3}
              maxLength={4000}
              className="rounded-xl bg-white border-zinc-200 text-sm resize-y min-h-[76px]"
            />
            {error && (
              <div data-testid="feedback-error" className="text-xs text-brand font-medium">{error}</div>
            )}
            <div className="flex items-center justify-end">
              <Button
                data-testid="feedback-send-btn"
                type="submit"
                disabled={submitting}
                className="rounded-full bg-brand hover:bg-brand-hover text-white h-10 px-5 text-sm font-semibold"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {submitting ? t("banner.sending") : t("banner.send")}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Dialog open={thankOpen} onOpenChange={setThankOpen}>
        <DialogContent data-testid="feedback-thankyou-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{t("banner.thankYouTitle")}</DialogTitle>
            <DialogDescription className="text-base text-zinc-700 pt-2 leading-relaxed">
              {t("banner.thankYouBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="feedback-thankyou-close"
              onClick={() => setThankOpen(false)}
              className="rounded-full bg-brand hover:bg-brand-hover text-white"
            >
              {t("banner.thankYouClose")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
